import { liveCountries, markets, registeredCountries } from "@/config/markets";
import { appLocales, defaultAppLocale, type AppLocale } from "@/i18n/config";

export const locales = appLocales;

export type LocaleType = AppLocale;

/**
 * Folds an arbitrary locale segment into a valid app locale, always returning
 * one (never undefined). Used by the /verify route to normalize the locale Spond
 * Core bakes into the verification-email link — Core emits Java-canonical tags
 * (`en-GB`, `no-NO`) or a bare language (`en`) that Discover's lowercase-only
 * router won't match. Differs from {@link mapShortLocaleToFull}, which is
 * case-sensitive, short-code-only, and may return undefined for its callers.
 */
export function resolveLocaleSegment(raw: string): LocaleType {
  const lower = raw.toLowerCase();
  // Region-bearing tag in any case (e.g. "en-GB" → "en-gb") matches directly.
  if (locales.includes(lower as LocaleType)) return lower as LocaleType;
  // Otherwise resolve by language prefix ("no-no" → "no", "en" → "en").
  const language = lower.split("-")[0];
  return mapShortLocaleToFull(language) ?? defaultAppLocale;
}

/**
 * Folds a group locale from the event document into a registered market
 * locale, including markets that are not live yet. Missing, empty, and
 * unknown values fall back to `en-gb`. This only normalizes the document
 * value. `resolveAppLocale` decides whether that locale is served.
 */
export function resolveRegisteredLocale(
  raw: string | null | undefined,
): AppLocale {
  const trimmed = raw?.trim();
  if (!trimmed) return defaultAppLocale;

  const lower = trimmed.toLowerCase();
  const registered = registeredCountries.map((code) => markets[code]);
  const byLocale = registered.find((market) => market.locale === lower);
  if (byLocale) return byLocale.locale;

  const language = lower.split("-")[0];
  const byLanguage = registered.find(({ languages }) =>
    (languages as readonly string[]).includes(language),
  );
  return byLanguage?.locale ?? defaultAppLocale;
}

export function mapShortLocaleToFull(
  shortLocale: string,
): LocaleType | undefined {
  const market = liveCountries
    .map((code) => markets[code])
    .find(({ languages }) =>
      (languages as readonly string[]).includes(shortLocale),
    );
  return (
    market?.locale ??
    (locales.includes(shortLocale as LocaleType)
      ? (shortLocale as LocaleType)
      : undefined)
  );
}
